/**
 * HomePage — Phase 18.15 + 18.15.7 fully CMS-driven.
 *
 * Hero, service cards, network section, routes header, CTA bar, trust
 * bullets and trusted-brands header all hydrate from the backoffice via:
 *   GET /api/website/homepage-settings
 *   GET /api/website/homepage-service-cards
 *
 * If the API returns nothing (cold start, network glitch) we render
 * production-safe defaults that match the current live homepage so the
 * page never looks broken.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { customerPublicCopy } from "../lib/customerPublicCopy";
import {
  ArrowRight,
  Ship,
  Plane,
  Package,
  Truck,
  Car,
  MapPin,
  Briefcase,
  User,
  CheckCircle2,
  Clock,
  ShieldCheck,
  Banknote,
  Headphones,
  Sparkles,
  ChevronRight,
  Info,
} from "lucide-react";

import SEO from "../components/SEO";
// Phase SEO Q1 (Feb 2026): StructuredData removed; site-wide schemas via <SiteSchema /> in App.js.
import TrackingWidget from "../components/TrackingWidget";
import TrustedBrandsMarquee from "../components/TrustedBrandsMarquee";
import RelatedDestinations from "../components/RelatedDestinations";
import { useConfig } from "../context/ConfigContext";
import { useBranding } from "../context/BrandingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Phase 18.15.5 — ALL service cards share ONE consistent brand gradient.
const SERVICE_ACCENT = "from-[#082c50] via-[#1a5193] to-[#194d8d]";

// Icon registry — keep keys short, lowercased. Admin selects a key in
// the backoffice; we resolve to the Lucide component here.
const ICON_REGISTRY = {
  ship: Ship,
  plane: Plane,
  package: Package,
  truck: Truck,
  car: Car,
  map: MapPin,
  briefcase: Briefcase,
  user: User,
  check: CheckCircle2,
  clock: Clock,
  shield: ShieldCheck,
  banknote: Banknote,
  support: Headphones,
  info: Info,
};
const resolveIcon = (key) => ICON_REGISTRY[String(key || "").toLowerCase()] || Package;

// -------- safe production-style fallbacks --------------------------------
const FALLBACK_SETTINGS = {
  hero: {
    eyebrow: "Cargo with a smile",
    title_lead: "Van Nederland naar de Caribbean —",
    title_highlight: "cargo met een smile.",
    subtitle:
      "Verzend pakketten, dozen, pallets, verhuisgoederen, voertuigen en zakelijke cargo vanuit Nederland naar de Caribbean — via zeevracht en luchtvracht.",
    cta_primary_label: "Vraag offerte aan",
    cta_primary_url: "/contact?get-quote=1#quote-form",
    cta_secondary_label: "Track uw zending",
    cta_secondary_url: "#tracking-widget",
    cta_tertiary_label: "Mijn 5999Cargo",
    cta_tertiary_url: "/my5999",
    trust_bullets: [
      { icon: "check", label: "All-in pricing — geen verborgen kosten" },
      { icon: "clock", label: "Wekelijkse vertrekken vanuit Rotterdam" },
      { icon: "shield", label: "Verzekerd én volledig getrackt" },
      { icon: "support", label: "Persoonlijk support op werkdagen" },
    ],
  },
  services_header: {
    eyebrow: "Onze services",
    title: "Hoe kunnen we u helpen?",
    subtitle:
      "Particulier of zakelijk — wij verzenden alles van losse pakketten tot complete verhuisinboedels en voertuigen.",
    link_label: "Alle services aanvragen",
    link_url: "/contact?get-quote=1#quote-form",
  },
  network: {
    eyebrow: "Ons netwerk",
    title: "Wekelijkse vertrekken naar het Caribisch gebied.",
    subtitle:
      "Vanuit Rotterdam bedienen we het hele Caribisch gebied — met zeevracht, luchtvracht en lokale partners op elk eiland. Persoonlijke planning, full tracking en duidelijke communicatie van begin tot eind.",
    kpis: [
      { value: "15+", label: "Bestemmingen" },
      { value: "1.200+", label: "Zendingen" },
      { value: "12 jr", label: "Ervaring" },
    ],
  },
  routes_header: {
    eyebrow: "Populaire routes & bestemmingen",
    tip_text: "Tijden indicatief",
    cta_label: "Vraag offerte voor uw route",
    cta_url: "/contact?get-quote=1#quote-form",
  },
  cta_band: {
    title: "Klaar om uw cargo te verzenden?",
    subtitle:
      "Ontvang binnen 30 seconden een offerte op maat. Geen verborgen kosten, persoonlijk contact, en realtime tracking vanaf het eerste moment.",
    badges: [
      { icon: "banknote", title: "Instant offerte", subtitle: "binnen 30 sec" },
      { icon: "shield", title: "All-in tarief", subtitle: "geen verrassingen" },
      { icon: "support", title: "Persoonlijk", subtitle: "support team" },
    ],
    cta_primary_label: "Vraag direct offerte aan",
    cta_primary_url: "/contact?get-quote=1#quote-form",
    cta_secondary_label: "Of bel ons direct",
    cta_secondary_url: "tel:+31488235999",
  },
};

const FALLBACK_SERVICE_CARDS = [
  { id: "fb-zee", icon: "ship", title: "Zeevracht", description: "Zware en grote zendingen voordelig per container.", badge: "Indicatieve planning", link_url: "/sea-freight", sort_order: 10 },
  { id: "fb-lucht", icon: "plane", title: "Luchtvracht", description: "Snellere optie voor urgente zendingen.", badge: "Snellere optie", link_url: "/air-freight", sort_order: 20 },
  { id: "fb-shop", icon: "package", title: "Shop, Drop & Ship", description: "Online shoppen in NL, wij leveren af.", badge: "All-in tarieven", link_url: "/shop-drop-ship", sort_order: 30 },
  { id: "fb-verhuis", icon: "truck", title: "Verhuizing", description: "Compleet verzorgde emigratie of remigratie.", badge: "Door to door", link_url: "/moving", sort_order: 40 },
  { id: "fb-voertuig", icon: "car", title: "Voertuig verzenden", description: "Auto's, motoren en boten veilig verscheept.", badge: "Veilig en verzekerd", link_url: "/sea-freight", sort_order: 50 },
  { id: "fb-pickup", icon: "map", title: "Pickup & Delivery", description: "Wij halen op bij u thuis of op uw bedrijf.", badge: "Heel Nederland", link_url: "/pickup-delivery", sort_order: 60 },
  { id: "fb-zakelijk", icon: "briefcase", title: "Zakelijke zendingen", description: "B2B cargo en palletvracht voor bedrijven.", badge: "Custom contract", link_url: "/contact?get-quote=1", sort_order: 70 },
  { id: "fb-my5999", icon: "user", title: "Mijn 5999Cargo", description: "Uw persoonlijke dashboard voor al uw zendingen.", badge: "Direct toegang", link_url: "/mijn-5999cargo", sort_order: 80 },
];

const FALLBACK_ROUTES = [
  { id: "fb-curacao", origin: "Nederland", destination: "Curaçao", methods_label: "Zeevracht & luchtvracht mogelijk", transit_text: "Vraag offerte aan voor actuele planning.", cta_primary_label: "Vraag offerte aan", cta_primary_url: "/contact?get-quote=1#quote-form" },
  { id: "fb-aruba", origin: "Nederland", destination: "Aruba", methods_label: "Zeevracht & luchtvracht mogelijk", transit_text: "Vraag offerte aan voor actuele planning.", cta_primary_label: "Vraag offerte aan", cta_primary_url: "/contact?get-quote=1#quote-form" },
  { id: "fb-bonaire", origin: "Nederland", destination: "Bonaire", methods_label: "Zeevracht & luchtvracht mogelijk", transit_text: "Vraag offerte aan voor actuele planning.", cta_primary_label: "Vraag offerte aan", cta_primary_url: "/contact?get-quote=1#quote-form" },
  { id: "fb-sxm", origin: "Nederland", destination: "Sint Maarten", methods_label: "Op aanvraag", transit_text: "Vraag offerte aan voor actuele planning.", cta_primary_label: "Vraag offerte aan", cta_primary_url: "/contact?get-quote=1#quote-form" },
  { id: "fb-sur", origin: "Nederland", destination: "Suriname", methods_label: "Op aanvraag", transit_text: "Vraag offerte aan voor actuele planning.", cta_primary_label: "Vraag offerte aan", cta_primary_url: "/contact?get-quote=1#quote-form" },
];

const SECTION = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";

// Merge backend section onto fallback so partial admin updates can't
// leave any key undefined (which would render "undefined" in the DOM).
function mergeSection(remote, fallback) {
  if (!remote || typeof remote !== "object") return fallback;
  const merged = { ...fallback, ...remote };
  // Lists need special handling — non-array remote means use fallback
  Object.keys(fallback).forEach((k) => {
    if (Array.isArray(fallback[k]) && !Array.isArray(merged[k])) {
      merged[k] = fallback[k];
    }
  });
  return merged;
}

export default function HomePage() {
  const { phone, email, whatsapp } = useConfig();
  const { homepageHeroUrl, homepageHeroAlt, hasCustomHomepageHero } = useBranding();

  const [settings, setSettings] = useState(FALLBACK_SETTINGS);
  const [serviceCards, setServiceCards] = useState(FALLBACK_SERVICE_CARDS);
  const [routes, setRoutes] = useState(FALLBACK_ROUTES);
  const [routesLoaded, setRoutesLoaded] = useState(false);

  // Fetch homepage settings
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/website/homepage-settings`, { timeout: 8000 })
      .then((res) => {
        if (cancelled) return;
        const sections = res?.data?.sections || {};
        setSettings({
          hero: mergeSection(sections.hero, FALLBACK_SETTINGS.hero),
          services_header: mergeSection(sections.services_header, FALLBACK_SETTINGS.services_header),
          network: mergeSection(sections.network, FALLBACK_SETTINGS.network),
          routes_header: mergeSection(sections.routes_header, FALLBACK_SETTINGS.routes_header),
          cta_band: mergeSection(sections.cta_band, FALLBACK_SETTINGS.cta_band),
        });
      })
      .catch(() => { /* keep fallbacks */ });
    return () => { cancelled = true; };
  }, []);

  // Fetch service cards (only active)
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/website/homepage-service-cards`, { timeout: 8000 })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data?.cards) ? res.data.cards : [];
        if (list.length > 0) setServiceCards(list);
      })
      .catch(() => { /* keep fallback cards */ });
    return () => { cancelled = true; };
  }, []);

  // Fetch routes (already CMS-managed)
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/website/homepage-routes`, { timeout: 8000 })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data?.routes) ? res.data.routes : [];
        if (list.length > 0) setRoutes(list);
      })
      .catch(() => { /* keep fallback */ })
      .finally(() => !cancelled && setRoutesLoaded(true));
    return () => { cancelled = true; };
  }, []);

  const hero = customerPublicCopy(settings.hero);
  const servicesHeader = customerPublicCopy(settings.services_header);
  const network = customerPublicCopy(settings.network);
  const routesHeader = customerPublicCopy(settings.routes_header);
  const ctaBand = customerPublicCopy(settings.cta_band);

  return (
    <>
      <SEO
        title="5999Cargo Group Europe — Cargo with a smile naar Curaçao, Aruba & Caribbean"
        description="Verzend pakketten, dozen, pallets, verhuisgoederen en zakelijke cargo vanuit Nederland naar Curaçao, Aruba, Bonaire, Sint Maarten en Suriname. All-in tarieven, tracking en Mijn 5999Cargo."
        keywords="cargo Curaçao, zeevracht Caribbean, luchtvracht Aruba, tracking 5999Cargo, Mijn 5999Cargo, verhuizen naar Curaçao, voertuig verzenden Caribbean"
      />
      {/* Phase SEO Q1 (Feb 2026): LocalBusiness + Organization + WebSite worden
          nu site-breed geleverd door <SiteSchema /> in App.js. De legacy
          inline LocalBusiness-JSON-LD is verwijderd om duplicate @id-schema
          te voorkomen. */}

      {/* ============================================================ HERO */}
      <section className="relative overflow-hidden bg-[#082c50] text-white" data-testid="home-hero">
        <div className="absolute inset-0">
          {hasCustomHomepageHero && homepageHeroUrl ? (
            <>
              <img
                src={homepageHeroUrl}
                alt={homepageHeroAlt || ""}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="home-hero-bg-image"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#082c50]/85 via-[#082c50]/65 to-[#1a5193]/55" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#082c50]/55 via-transparent to-transparent" />
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-[#082c50] via-[#0a335c] to-[#194d8d]" />
              <div className="absolute -top-32 -left-24 w-[36rem] h-[36rem] rounded-full bg-cyan-400/10 blur-[120px]" />
              <div className="absolute top-32 -right-32 w-[40rem] h-[40rem] rounded-full bg-[#1a5193]/40 blur-[140px]" />
            </>
          )}
        </div>

        <div className={`relative ${SECTION} pt-16 pb-40 sm:pb-44 lg:pt-24 lg:pb-56`}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            {hero.eyebrow ? (
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] uppercase tracking-[0.18em] font-semibold mb-6">
                <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
                {hero.eyebrow}
              </span>
            ) : null}
            <h1 className="font-bold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-h1">
              {hero.title_lead}{" "}
              <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-white bg-clip-text text-transparent">
                {hero.title_highlight}
              </span>
            </h1>
            <p className="mt-5 text-white/80 text-lg md:text-xl max-w-2xl leading-relaxed">
              {hero.subtitle}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:items-center">
              {hero.cta_primary_label && hero.cta_primary_url ? (
                <Link
                  to={hero.cta_primary_url}
                  className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-xl bg-white text-[#082c50] font-bold tracking-tight text-base hover:bg-cyan-50 active:scale-[0.98] transition-all shadow-[0_12px_24px_-12px_rgba(0,0,0,0.5)]"
                  data-testid="hero-cta-quote"
                >
                  {hero.cta_primary_label}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              ) : null}
              {hero.cta_secondary_label && hero.cta_secondary_url ? (
                <a
                  href={hero.cta_secondary_url}
                  className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-xl bg-white/10 border-2 border-cyan-300/40 text-white hover:bg-white/15 hover:border-cyan-300/80 hover:shadow-[0_0_0_4px_rgba(34,211,238,0.15)] font-semibold tracking-tight text-base transition-all backdrop-blur-sm"
                  data-testid="hero-cta-track"
                >
                  <Package className="w-4.5 h-4.5" />
                  {hero.cta_secondary_label}
                  <ChevronRight className="w-5 h-5" />
                </a>
              ) : null}
              {hero.cta_tertiary_label && hero.cta_tertiary_url ? (
                <Link
                  to={hero.cta_tertiary_url}
                  className="inline-flex items-center justify-center gap-2 h-14 px-6 rounded-xl bg-transparent border border-white/25 text-white hover:bg-white/[0.06] hover:border-white/55 font-semibold tracking-tight text-base transition-all"
                  data-testid="hero-cta-my5999"
                >
                  <User className="w-4.5 h-4.5" />
                  {hero.cta_tertiary_label}
                </Link>
              ) : null}
            </div>

            {Array.isArray(hero.trust_bullets) && hero.trust_bullets.length > 0 ? (
              <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl text-sm text-white/80">
                {hero.trust_bullets.map((t, i) => {
                  const Icon = resolveIcon(t.icon);
                  return (
                    <li key={i} className="flex items-center gap-2.5">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-400/20 text-emerald-300 flex items-center justify-center">
                        <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                      </span>
                      <span>{t.label}</span>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </motion.div>
        </div>
      </section>

      {/* Floating tracking widget */}
      <div className={`relative ${SECTION} -mt-28 sm:-mt-32 lg:-mt-40 z-20`} data-testid="home-tracking-wrapper">
        <TrackingWidget />
      </div>

      {/* ============================================================ SERVICES */}
      <section className="bg-[#F8FAFC] pt-8 sm:pt-12 lg:pt-16 pb-20 lg:pb-28" data-testid="home-services">
        <div className={SECTION}>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              {servicesHeader.eyebrow ? (
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#1a5193] font-bold">
                  {servicesHeader.eyebrow}
                </span>
              ) : null}
              <h2 className="font-bold text-3xl sm:text-4xl text-[#082c50] tracking-tight mt-3">
                {servicesHeader.title}
              </h2>
              {servicesHeader.subtitle ? (
                <p className="mt-3 text-[#514e4e] max-w-xl">{servicesHeader.subtitle}</p>
              ) : null}
            </div>
            {servicesHeader.link_label && servicesHeader.link_url ? (
              <Link
                to={servicesHeader.link_url}
                className="text-sm font-bold tracking-tight text-[#1a5193] hover:text-[#082c50] inline-flex items-center gap-1.5"
                data-testid="services-header-link"
              >
                {servicesHeader.link_label} <ArrowRight className="w-4 h-4" />
              </Link>
            ) : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {customerPublicCopy(serviceCards).map((s, i) => {
              const Icon = resolveIcon(s.icon);
              return (
                <motion.div
                  key={s.id || s.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  data-testid={`service-card-${i}`}
                >
                  <Link
                    to={s.link_url || "/contact?get-quote=1"}
                    className="group relative block h-full bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#082c50]/40 hover:-translate-y-1 hover:shadow-[0_18px_38px_-22px_rgba(8,44,80,0.3)] transition-all overflow-hidden"
                  >
                    <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${SERVICE_ACCENT} opacity-10 group-hover:opacity-20 transition-opacity`} />
                    <div className={`relative inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${SERVICE_ACCENT} text-white shadow-sm`}>
                      <Icon className="w-6 h-6" strokeWidth={2} />
                    </div>
                    <h3 className="relative font-bold text-lg text-[#082c50] mt-4 tracking-tight">
                      {s.title}
                    </h3>
                    {s.description ? (
                      <p className="relative text-sm text-[#514e4e] mt-1.5 leading-relaxed">
                        {s.description}
                      </p>
                    ) : null}
                    <div className="relative mt-4 flex items-center justify-between pt-3 border-t border-slate-100">
                      {s.badge ? (
                        <span className="text-[11px] uppercase tracking-[0.16em] text-[#1a5193] font-bold">
                          {s.badge}
                        </span>
                      ) : <span />}
                      <ArrowRight className="w-4 h-4 text-[#1a5193] group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================ NETWORK */}
      <section className="relative overflow-hidden bg-[#082c50] text-white py-20 lg:py-28" data-testid="home-network">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="absolute -bottom-32 right-0 w-[40rem] h-[40rem] rounded-full bg-[#1a5193]/30 blur-3xl" />
        </div>

        <div className={`relative ${SECTION}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              {network.eyebrow ? (
                <span className="text-[11px] uppercase tracking-[0.2em] text-cyan-300 font-bold">
                  {network.eyebrow}
                </span>
              ) : null}
              <h2 className="font-bold text-3xl sm:text-4xl tracking-tight mt-3 leading-tight">
                {network.title}
              </h2>
              {network.subtitle ? (
                <p className="mt-4 text-white/75 leading-relaxed max-w-md">{network.subtitle}</p>
              ) : null}

              {Array.isArray(network.kpis) && network.kpis.length > 0 ? (
                <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
                  {network.kpis.map((k, i) => (
                    <div key={i} className="rounded-xl bg-white/[0.05] border border-white/10 backdrop-blur p-4">
                      <div className="text-2xl font-bold tracking-tight">{k.value}</div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-white/60 mt-1">{k.label}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur p-2 sm:p-4">
              <div className="rounded-2xl bg-[#082c50]/60 border border-white/5">
                <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-cyan-300 font-bold">
                    {routesHeader.eyebrow}
                  </span>
                  {routesHeader.tip_text ? (
                    <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-white/55">
                      <Info className="w-3 h-3" />
                      {routesHeader.tip_text}
                    </span>
                  ) : null}
                </div>
                <ul className="divide-y divide-white/5" data-testid="home-routes-list">
                  {routes.map((r, i) => (
                    <RouteListItem key={r.id || `${r.origin}-${r.destination}-${i}`} route={r} />
                  ))}
                </ul>
                <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap">
                  {routesHeader.cta_label && routesHeader.cta_url ? (
                    <Link
                      to={routesHeader.cta_url}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300 hover:text-white transition-colors"
                      data-testid="home-routes-cta"
                    >
                      {routesHeader.cta_label} <ArrowRight className="w-4 h-4" />
                    </Link>
                  ) : <span />}
                  {!routesLoaded && (
                    <span className="text-[10px] uppercase tracking-[0.16em] text-white/45">laden…</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ DESTINATIONS CROSSLINK (SEO Sprint 4) */}
      <RelatedDestinations
        testid="home-destinations"
        eyebrow="Populaire bestemmingen"
        title="Naar welke Caribbean-bestemming verzendt u?"
        description="Curaçao, Aruba, Bonaire, Sint Maarten of Suriname — wij verzorgen pakketten, cargo en verhuiszendingen vanuit Nederland naar elk van deze bestemmingen. Bekijk per bestemming wat wij doen."
        anchorTemplate="Verzenden naar {name}"
        variant="card"
      />

      {/* ============================================================ TRUST STRIP */}
      <TrustedBrandsMarquee />

      {/* ============================================================ CTA BAR */}
      <section className="relative overflow-hidden bg-[#082c50] text-white py-16 lg:py-20" data-testid="home-cta-bar">
        <div className="absolute inset-0">
          <div className="absolute -left-32 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="absolute right-0 bottom-0 w-[30rem] h-[30rem] rounded-full bg-[#1a5193]/30 blur-3xl" />
        </div>

        <div className={`relative ${SECTION}`}>
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr,0.8fr] gap-8 items-center">
            <div>
              <h3 className="font-bold text-3xl sm:text-4xl tracking-tight leading-tight">
                {ctaBand.title}
              </h3>
              {ctaBand.subtitle ? (
                <p className="mt-3 text-white/75 max-w-xl leading-relaxed">{ctaBand.subtitle}</p>
              ) : null}

              {Array.isArray(ctaBand.badges) && ctaBand.badges.length > 0 ? (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
                  {ctaBand.badges.map((x, i) => {
                    const Icon = resolveIcon(x.icon);
                    return (
                      <div key={i} className="rounded-xl bg-white/[0.05] border border-white/10 backdrop-blur p-3.5">
                        <Icon className="w-5 h-5 text-cyan-300" />
                        <div className="font-bold text-sm text-white mt-2">{x.title}</div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-white/60 mt-0.5">{x.subtitle}</div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-stretch">
              {ctaBand.cta_primary_label && ctaBand.cta_primary_url ? (
                <Link
                  to={ctaBand.cta_primary_url}
                  className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-xl bg-white text-[#082c50] font-bold tracking-tight text-base hover:bg-cyan-50 active:scale-[0.98] transition-all"
                  data-testid="cta-bar-quote"
                >
                  {ctaBand.cta_primary_label}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              ) : null}
              {ctaBand.cta_secondary_label && ctaBand.cta_secondary_url ? (
                <a
                  href={ctaBand.cta_secondary_url}
                  className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-xl bg-white/0 border border-white/30 text-white hover:bg-white/10 hover:border-white/60 font-semibold tracking-tight text-base transition-all"
                  data-testid="cta-bar-call"
                >
                  {ctaBand.cta_secondary_label}
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}


function pickRouteIcon(methodsLabel) {
  const m = (methodsLabel || "").toLowerCase();
  if (m.includes("lucht") && !m.includes("zee")) return Plane;
  if (m.includes("op aanvraag")) return Package;
  return Ship;
}

function RouteListItem({ route }) {
  const Icon = pickRouteIcon(route.methods_label);
  return (
    <li className="px-5 py-4 hover:bg-white/[0.03] transition-colors" data-testid={`home-route-${route.destination_slug || route.id || ""}`}>
      <div className="flex items-start gap-4">
        <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center mt-0.5">
          <Icon className="w-4.5 h-4.5" strokeWidth={2} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center text-sm flex-wrap gap-y-1">
            <span className="font-semibold text-white">{route.origin}</span>
            <ArrowRight className="w-3.5 h-3.5 mx-2 text-white/50" />
            <span className="font-semibold text-white">{route.destination}</span>
            {route.badge ? (
              <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-[0.16em] font-bold text-cyan-200 bg-cyan-500/15 border border-cyan-300/20 rounded-full px-2 py-0.5">
                {route.badge}
              </span>
            ) : null}
          </div>
          {route.methods_label ? (
            <div className="text-[11px] uppercase tracking-[0.16em] text-white/55 font-semibold mt-1">
              {route.methods_label}
            </div>
          ) : null}
          {route.transit_text ? (
            <p className="text-xs text-white/75 mt-1.5 leading-relaxed">{route.transit_text}</p>
          ) : null}
          {route.disclaimer_text ? (
            <p className="text-[11px] text-white/50 mt-1 italic">{route.disclaimer_text}</p>
          ) : null}
          {(route.cta_primary_label && route.cta_primary_url) ||
          (route.cta_secondary_label && route.cta_secondary_url) ? (
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
              {route.cta_primary_label && route.cta_primary_url ? (
                <Link to={route.cta_primary_url} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] font-bold text-cyan-200 hover:text-white transition-colors" data-testid="route-cta-primary">
                  {route.cta_primary_label}
                  <ArrowRight className="w-3 h-3" />
                </Link>
              ) : null}
              {route.cta_secondary_label && route.cta_secondary_url ? (
                <Link to={route.cta_secondary_url} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] font-bold text-white/55 hover:text-white transition-colors" data-testid="route-cta-secondary">
                  {route.cta_secondary_label}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}
